import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {ActionList, ActionMenu, Banner, Checkbox, FormControl, Link} from '@primer/react'
import debounce from 'lodash-es/debounce'
import {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import type {Repository} from '../../api/common-contracts'
import {getInitialState} from '../../helpers/initial-state'
import {validateBulkAddQuery} from '../../helpers/validate-bulk-add-query'
import {useAutoAddItemCount} from '../../state-providers/auto-add-item-count/auto-add-item-count-provider'
import {TemplateDialogResources} from '../../strings'
import {RepositoryPicker} from '../automation/repository-picker'
import {TokenizedFilterInput} from '../filter-bar/tokenized-filter-input'
import styles from './TemplateDialog.module.css'

const QUERY_DEBOUNCE_MS = 500

function ServerErrorBanner({onDismiss}: {onDismiss: () => void}) {
  const bannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bannerRef.current?.focus()
  }, [])

  return (
    <div ref={bannerRef} tabIndex={-1} className={styles.ErrorBanner} role="region" aria-label="Error notification">
      <Banner variant="critical" onDismiss={onDismiss} role="alert" aria-live="polite">
        <Banner.Title>Server Error</Banner.Title>
        {TemplateDialogResources.serverErrorMessage}
      </Banner>
    </div>
  )
}

function RepositoryEntitiesPicker({
  entitiesText,
  onEntitiesChange,
  autoAddEnabledRepository,
  onRepositoryChange,
  currentEntities,
  templateLinkedRepositoryId,
}: {
  entitiesText: string
  onEntitiesChange: (entities: Array<'issues' | 'prs'>) => void
  autoAddEnabledRepository: Repository | null
  onRepositoryChange?: (repository?: Repository) => void
  currentEntities?: Array<'issues' | 'prs'>
  templateLinkedRepositoryId?: number
}) {
  const {refreshAutoAddItemCount} = useAutoAddItemCount()

  const handleEntitiesChange = useCallback(
    (entities: Array<'issues' | 'prs'>) => {
      onEntitiesChange(entities)

      if (autoAddEnabledRepository && entities.length > 0) {
        let additionalQuery = ''
        if (entities.length === 1) {
          additionalQuery = entities[0] === 'issues' ? 'is:issue' : 'is:pr'
        }
        const repoQuery = `${additionalQuery} is:open`
        refreshAutoAddItemCount({repositoryId: autoAddEnabledRepository.id, query: repoQuery})
      }
    },
    [onEntitiesChange, autoAddEnabledRepository, refreshAutoAddItemCount],
  )

  const handleRepositoryChange = useCallback(
    (repository?: Repository) => {
      onRepositoryChange?.(repository)

      if (repository && currentEntities && currentEntities.length > 0) {
        let additionalQuery = ''
        if (currentEntities.length === 1) {
          additionalQuery = currentEntities[0] === 'issues' ? 'is:issue' : 'is:pr'
        }
        const repoQuery = `${additionalQuery} is:open`
        refreshAutoAddItemCount({repositoryId: repository.id, query: repoQuery})
      }
    },
    [onRepositoryChange, currentEntities, refreshAutoAddItemCount],
  )

  return (
    <div className={styles.BulkAddConfigurationActions}>
      <ActionMenu>
        <ActionMenu.Button>{entitiesText}</ActionMenu.Button>
        <ActionMenu.Overlay>
          <ActionList aria-label="Select entities to add">
            <ActionList.Item
              onSelect={() => {
                handleEntitiesChange(['issues'])
              }}
            >
              {TemplateDialogResources.openIssues}
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => {
                handleEntitiesChange(['prs'])
              }}
            >
              {TemplateDialogResources.openPullRequests}
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => {
                handleEntitiesChange(['issues', 'prs'])
              }}
            >
              {TemplateDialogResources.openIssuesAndPullRequests}
            </ActionList.Item>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <span>from</span>
      <RepositoryPicker
        targetRepositoryId={autoAddEnabledRepository?.id ?? templateLinkedRepositoryId}
        onRepositorySelected={handleRepositoryChange}
        isEditing
        shouldReset={false}
      />
    </div>
  )
}

const getInitialQuery = (defaultRepository?: Repository | null) =>
  `state:open ${defaultRepository ? `repo:${defaultRepository.nameWithOwner} ` : ''}`

const BULK_ADD_CONTEXT = {
  searchType: 'global' as const,
}

/**
 * Validates search input using the pure validation helper.
 * Returns an error message if validation fails, undefined if valid.
 */
export const validateInput = (input: string): string | undefined => {
  const {isEnterpriseRuntime, isMultiTenantEnterprise} = getInitialState()

  // The validation function no longer requires user data and relies on environment flags
  const result = validateBulkAddQuery(input, {isEnterpriseRuntime, isMultiTenantEnterprise})

  return result.isValid ? undefined : result.error
}

function QueryPicker({
  autoAddEnabledRepository,
  onQueryChange,
  errorMessage,
  queryInputRef,
}: {
  autoAddEnabledRepository: Repository | null
  onQueryChange?: (query: string) => void
  errorMessage?: string | null
  queryInputRef?: React.RefObject<HTMLInputElement | null>
}) {
  const initialQuery = useMemo(() => getInitialQuery(autoAddEnabledRepository), [autoAddEnabledRepository])
  const [query, setQuery] = useState(initialQuery)
  const localInputRef = useRef<HTMLInputElement>(null)
  const inputRef = queryInputRef || localInputRef
  const {refreshAutoAddItemCount} = useAutoAddItemCount()

  const debouncedQueryCount = useMemo(
    () =>
      debounce(async (queryString: string) => {
        refreshAutoAddItemCount({query: queryString})
      }, QUERY_DEBOUNCE_MS),
    [refreshAutoAddItemCount],
  )

  useEffect(() => {
    return () => {
      debouncedQueryCount.cancel()
    }
  }, [debouncedQueryCount])

  useEffect(() => {
    onQueryChange?.(initialQuery)
  }, [initialQuery, onQueryChange])

  const handleQueryChange = useCallback(
    (newQuery: string) => {
      setQuery(newQuery)
      onQueryChange?.(newQuery)

      if (newQuery.trim()) {
        debouncedQueryCount(newQuery)
      } else {
        debouncedQueryCount.cancel()
      }
    },
    [onQueryChange, debouncedQueryCount],
  )

  return (
    <div className={styles.AdvancedSearchQueryInput}>
      <TokenizedFilterInput
        value={query}
        onChangeValue={handleQueryChange}
        inputRef={inputRef}
        hideSaveButton
        hideDisplayButton
        aria-describedby={errorMessage ? 'query-validation-message' : undefined}
        context={BULK_ADD_CONTEXT}
      />
      {errorMessage && (
        <FormControl.Validation variant="error" id="query-validation-message">
          {errorMessage}
        </FormControl.Validation>
      )}
    </div>
  )
}

export type SubmissionResult = 'success' | 'validation_error' | 'server_error'

/**
 * Exposed methods and state from the BulkAddConfiguration component
 */
export interface BulkAddConfigurationRef {
  /** Validates and submits the current query */
  validateQuery: () => Promise<SubmissionResult>
  /** Displays a server error banner to the user */
  showServerError: () => void
  /** Gets whether bulk add is currently enabled */
  isBulkAddEnabled: () => boolean
  /** Gets whether query mode is currently active (vs repository mode) */
  isQueryMode: () => boolean
}

/**
 * Props for the BulkAddConfiguration component
 */
export interface BulkAddConfigurationProps {
  ref?: React.Ref<BulkAddConfigurationRef>
  entitiesText: string
  onEntitiesChange: (entities: Array<'issues' | 'prs'>) => void
  autoAddEnabledRepository: Repository | null
  onRepositoryChange?: (repository?: Repository) => void
  onQueryChange?: (query: string) => void
  /** Callback to receive count from API preview */
  onQueryPreviewResult?: (count: number) => void
  currentQuery?: string
  /** Current selected entities for repository dropdown flow */
  currentEntities?: Array<'issues' | 'prs'>
}

export const BulkAddConfiguration = ({
  ref,
  entitiesText,
  onEntitiesChange,
  autoAddEnabledRepository,
  onRepositoryChange,
  onQueryChange,
  onQueryPreviewResult: _onQueryPreviewResult,
  currentQuery,
  currentEntities,
}: BulkAddConfigurationProps) => {
  const {templateLinkedRepositoryId} = getInitialState()
  const [enableBulkAdd, setEnableBulkAdd] = useState(true)
  const [queryError, setQueryError] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)
  const queryInputRef = useRef<HTMLInputElement>(null)
  const serverErrorRef = useRef<string | null>(null)
  const queryErrorRef = useRef<string | null>(null)

  useEffect(() => {
    // maintained so that it will only clear when user types
    serverErrorRef.current = serverError
  }, [serverError])

  useEffect(() => {
    // maintained so that it will only clear when user types
    queryErrorRef.current = queryError
  }, [queryError])

  const memex_create_project_from_query = useFeatureFlag('memex_create_project_from_query')
  const [useQueryMode, setUseQueryMode] = useState(false)

  const handleToggleMode = useCallback(() => {
    setUseQueryMode(prev => !prev)
    setQueryError(null)
    setServerError(null)
  }, [])

  const handleQueryChange = useCallback(
    (newQuery: string) => {
      if (queryErrorRef.current) {
        setQueryError(null)
      }
      if (serverErrorRef.current) {
        setServerError(null)
      }
      onQueryChange?.(newQuery)
    },
    [onQueryChange],
  )

  const validateQuery = useCallback(async (): Promise<SubmissionResult> => {
    // Don't block template form submission if the bulk add option is unchecked by user
    if (!enableBulkAdd) {
      return 'success'
    }

    if (!currentQuery?.trim()) {
      setQueryError(TemplateDialogResources.invalidNoQuery)
      queryInputRef.current?.focus()
      return 'validation_error'
    }

    setQueryError(null)
    setServerError(null)

    const validationError = validateInput(currentQuery)
    if (validationError) {
      setQueryError(validationError)
      queryInputRef.current?.focus()
      return 'validation_error'
    }

    return 'success'
  }, [enableBulkAdd, currentQuery])

  const showServerError = useCallback(() => {
    setServerError(TemplateDialogResources.serverErrorMessage)
  }, [])

  const isBulkAddEnabled = useCallback(() => {
    return enableBulkAdd
  }, [enableBulkAdd])

  const isQueryModeEnabled = useCallback(() => {
    return useQueryMode
  }, [useQueryMode])

  useImperativeHandle(
    ref,
    () => ({
      validateQuery,
      showServerError,
      isBulkAddEnabled,
      isQueryMode: isQueryModeEnabled,
    }),
    [validateQuery, showServerError, isBulkAddEnabled, isQueryModeEnabled],
  )

  const onEnableBulkAddChange = useCallback(() => {
    const newEnableBulkAdd = !enableBulkAdd
    setEnableBulkAdd(newEnableBulkAdd)
    if (!newEnableBulkAdd) {
      onRepositoryChange?.(undefined)
    }
  }, [enableBulkAdd, setEnableBulkAdd, onRepositoryChange])

  return (
    <div className={styles.BulkAddConfigurationContainer}>
      {serverError && <ServerErrorBanner onDismiss={() => setServerError(null)} />}

      <FormControl>
        <Checkbox
          checked={enableBulkAdd}
          onChange={onEnableBulkAddChange}
          aria-describedby="bulk-add-description"
          data-testid="bulk-add-checkbox"
        />
        <FormControl.Label className={styles.formControlLabel}>
          {memex_create_project_from_query
            ? useQueryMode
              ? TemplateDialogResources.bulkAddSearch
              : TemplateDialogResources.bulkAddRepository
            : TemplateDialogResources.bulkAddTitle}
          {memex_create_project_from_query && useQueryMode && <BetaLabel className={styles.betaLabel} />}
        </FormControl.Label>
        <FormControl.Caption id="bulk-add-description">
          {memex_create_project_from_query
            ? useQueryMode
              ? TemplateDialogResources.bulkAddSubtitleSearch
              : TemplateDialogResources.bulkAddSubtitleRepository
            : TemplateDialogResources.bulkAddSubtitle}
        </FormControl.Caption>
      </FormControl>

      {enableBulkAdd &&
        (memex_create_project_from_query ? (
          <>
            {useQueryMode ? (
              <QueryPicker
                autoAddEnabledRepository={autoAddEnabledRepository}
                onQueryChange={handleQueryChange}
                errorMessage={queryError}
                queryInputRef={queryInputRef}
              />
            ) : (
              <RepositoryEntitiesPicker
                entitiesText={entitiesText}
                onEntitiesChange={onEntitiesChange}
                autoAddEnabledRepository={autoAddEnabledRepository}
                onRepositoryChange={onRepositoryChange}
                currentEntities={currentEntities}
                templateLinkedRepositoryId={templateLinkedRepositoryId}
              />
            )}
            <p className={styles.ModeToggleLink}>
              Add from a{' '}
              <Link as="button" inline onClick={handleToggleMode}>
                {useQueryMode ? 'repository' : 'search query'}{' '}
              </Link>{' '}
              instead
            </p>
          </>
        ) : (
          <RepositoryEntitiesPicker
            entitiesText={entitiesText}
            onEntitiesChange={onEntitiesChange}
            autoAddEnabledRepository={autoAddEnabledRepository}
            onRepositoryChange={onRepositoryChange}
            currentEntities={currentEntities}
            templateLinkedRepositoryId={templateLinkedRepositoryId}
          />
        ))}
    </div>
  )
}

BulkAddConfiguration.displayName = 'BulkAddConfiguration'

try{ ServerErrorBanner.displayName ||= 'ServerErrorBanner' } catch {}
try{ RepositoryEntitiesPicker.displayName ||= 'RepositoryEntitiesPicker' } catch {}
try{ QueryPicker.displayName ||= 'QueryPicker' } catch {}